import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useParams } from 'react-router-dom'
import Header from '@/components/layout/Header'
import AuditCtaButton from '@/components/ui/AuditCtaButton'
import Contact from '@/components/sections/Contact'
import Footer from '@/components/layout/Footer'
import { colors } from '@/styles/colors'
import { fetchContactContent, fetchSiteMetadata } from '@/api/cmsService'
import {
  cmsSlugsForRoute,
  loadServicePageForRoute,
  normalizeServiceRouteId,
} from '@/api/servicePageResolver'
import { cmsDebug, isCmsDebugEnabled } from '@/api/helpers'
import { cmsParagraph } from '@/styles/cmsText'
import type { MetadataContent, ServicePageContent } from '@/api/types'
import SEOHead from '@/components/SEO/SEOHead'
import { buildServicePageJsonLd, resolveSeo } from '@/seo/resolveSeo'
import {
  ApproachDecorativeArrows,
  DesktopApproachSection,
  DesktopProblemCardsSection,
  DesktopTechnologySection,
  DesktopWorkflowSection,
  MobileApproachSection,
  MobileProblemCardsSection,
  MobileTechnologySection,
  MobileWorkflowSection,
} from '@/components/service-page/ServicePageCmsSections'

/** Desktop hero — CMS copy wraps fully in the centered column */
const SERVICE_HERO_TEXT_BOX: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
  gap: '24px',
  position: 'absolute',
  zIndex: 2,
  width: '1240px',
  maxWidth: 'calc(100% - 48px)',
  left: 'calc(50% - 1240px/2)',
  top: '280px',
  boxSizing: 'border-box',
}

const SERVICE_HERO_EYEBROW: CSSProperties = {
  width: '100%',
  fontFamily: "'Instrument Sans', sans-serif",
  fontWeight: 400,
  fontSize: '28px',
  lineHeight: '34px',
  textAlign: 'center',
  color: colors.neutral[95],
  margin: 0,
  letterSpacing: '7px',
  textTransform: 'uppercase',
  flexShrink: 0,
  ...cmsParagraph(),
}

const SERVICE_HERO_HEADLINE: CSSProperties = {
  width: '100%',
  fontFamily: "'Instrument Sans', sans-serif",
  fontWeight: 500,
  fontSize: '56px',
  lineHeight: '68px',
  textAlign: 'center',
  color: colors.neutral[95],
  margin: 0,
  ...cmsParagraph(),
}

function getInitialViewportScale(): number {
  if (typeof window === 'undefined') return 1
  if (window.innerWidth < 768) return 1
  return Math.min(window.innerWidth / 1920, 1)
}

const SERVICE_HERO_DESC: CSSProperties = {
  width: '100%',
  fontFamily: "'Instrument Sans', sans-serif",
  fontWeight: 400,
  fontSize: '32px',
  lineHeight: '39px',
  textAlign: 'center',
  color: colors.neutral[80],
  margin: 0,
  alignSelf: 'stretch',
  ...cmsParagraph(),
}

/** Extra gap between hero description and CTA (all service pages) */
const SERVICE_HERO_CTA_STYLE: CSSProperties = {
  marginTop: '24px',
}

export default function ServicePage() {
  const { serviceId } = useParams()
  const routeKey = normalizeServiceRouteId(serviceId)
  // Detect mobile on initial load to prevent flickering
  const detectMobile = () => {
    const isMobileWidth = window.innerWidth < 768
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return isMobileWidth || isMobileUA
  }

  const initialMobile = detectMobile()
  const [scale, setScale] = useState(getInitialViewportScale)
  const [isMobile, setIsMobile] = useState(initialMobile)
  const sectionRef = useRef<HTMLDivElement>(null)

  // Below-fold sections only — hero is always visible on load (above the fold)
  const [processVisible, setProcessVisible] = useState(initialMobile)
  const [businessProblemsVisible, setBusinessProblemsVisible] = useState(initialMobile)
  const [serviceContent, setServiceContent] = useState<ServicePageContent | null>(null)
  const [loadedRouteKey, setLoadedRouteKey] = useState<string | null>(null)
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [cmsNotFound, setCmsNotFound] = useState(false)
  const [siteMetadata, setSiteMetadata] = useState<MetadataContent | null>(null)
  const [contactInfo, setContactInfo] = useState<{
    email?: string
    phone?: string
    address?: string
  }>()
  // Refs for intersection observers
  const heroRef = useRef<HTMLDivElement>(null)
  const businessProblemsRef = useRef<HTMLDivElement>(null)
  const workflowRef = useRef<HTMLDivElement>(null)
  const processRef = useRef<HTMLDivElement>(null)
  const techRef = useRef<HTMLDivElement>(null)

  // Layout measurements (desktop)
  const heroHeight = 900
  const businessProblemsHeight = 1402  // New "If This Sounds Familiar" section
  const workflowSectionHeight = 714
  const processSectionHeight = 1401
  const techSectionHeight = 528  // Technologies section
  const afterTechSpacing = 60
  const footerOffsetFromContact = 1068
  const footerHeight = 608

  const businessProblemsTop = heroHeight
  const workflowTop = businessProblemsTop + businessProblemsHeight
  const processTop = workflowTop + workflowSectionHeight
  const techTop = processTop + processSectionHeight
  const contactTop = techTop + techSectionHeight + afterTechSpacing
  const footerTop = contactTop + footerOffsetFromContact
  const desktopDesignHeight = footerTop + footerHeight

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [routeKey])

  useLayoutEffect(() => {
    setServiceContent(null)
    setLoadedRouteKey(null)
    setCmsNotFound(false)
    setSiteMetadata(null)
    setIsPageLoading(true)
  }, [routeKey])

  useEffect(() => {
    const updateScale = () => {
      const viewportWidth = window.innerWidth
      const isMobileWidth = viewportWidth < 768
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const isMobileView = isMobileWidth || isMobileUA
      setIsMobile(isMobileView)

      if (isMobileView) {
        setScale(1)
      } else {
        const desktopDesignWidth = 1920
        const newScale = Math.min(viewportWidth / desktopDesignWidth, 1)
        setScale(newScale)
      }
    }

    updateScale()
    window.addEventListener('resize', updateScale)

    return () => {
      window.removeEventListener('resize', updateScale)
    }
  }, [])

  useEffect(() => {
    if (!routeKey) {
      setServiceContent(null)
      setLoadedRouteKey(null)
      setCmsNotFound(true)
      setIsPageLoading(false)
      return
    }

    let cancelled = false

    if (isCmsDebugEnabled()) {
      console.info('[DigiTrek] ServicePage loading CMS for route:', routeKey)
    }

    const loadServiceContent = async () => {
      try {
        const [resolved, contact, metadata] = await Promise.all([
          loadServicePageForRoute(serviceId),
          fetchContactContent(),
          fetchSiteMetadata(),
        ])
        if (cancelled) return

        const matched = resolved

        cmsDebug('ServicePage CMS', {
          routeParam: serviceId,
          routeKey,
          slugsTried: cmsSlugsForRoute(serviceId),
          resolvedSlug: resolved?.slug,
          resolvedTitle: resolved?.title,
          workflowCount: resolved?.workflow?.length ?? 0,
          approachCount: resolved?.approach?.length ?? 0,
          problemsCount: resolved?.problemCards?.length ?? 0,
          found: Boolean(matched),
        })

        setServiceContent(matched)
        setLoadedRouteKey(routeKey)
        setCmsNotFound(!matched)
        setContactInfo(contact)
        setSiteMetadata(metadata)
      } catch (err) {
        if (cancelled) return
        cmsDebug('ServicePage CMS load failed', err instanceof Error ? err.message : err)
        setServiceContent(null)
        setLoadedRouteKey(routeKey)
        setCmsNotFound(true)
        try {
          const [contactFallback, metadataFallback] = await Promise.all([
            fetchContactContent(),
            fetchSiteMetadata(),
          ])
          setContactInfo(contactFallback)
          setSiteMetadata(metadataFallback)
        } catch {
          /* keep prior contactInfo if any */
        }
      } finally {
        if (!cancelled) setIsPageLoading(false)
      }
    }

    void loadServiceContent()

    return () => {
      cancelled = true
    }
  }, [routeKey, serviceId])

  const pageContent =
    loadedRouteKey === routeKey && routeKey ? serviceContent : null

  const workflowItems = pageContent?.workflow ?? []
  const approachItems = pageContent?.approach ?? []
  const problemCards = pageContent?.problemCards ?? []
  const technologyItems = pageContent?.technology ?? []

  const pageSeo = useMemo(() => {
    const slug = pageContent?.slug ?? routeKey ?? ''
    const pathname = slug ? `/services/${slug}` : '/'
    const legacyTitle = pageContent?.title ? `${pageContent.title} | DIGITREK` : undefined

    return resolveSeo({
      cmsSeo: pageContent?.seo,
      globalSeo: siteMetadata?.seo,
      legacyTitle,
      legacyDescription: pageContent?.description || pageContent?.header,
      pathname,
      favicon: siteMetadata?.favicon,
      jsonLd: pageContent
        ? buildServicePageJsonLd({
            title: pageContent.title,
            description: pageContent.description || pageContent.header,
            slug: pageContent.slug,
          })
        : undefined,
    })
  }, [pageContent, routeKey, siteMetadata])

  // Intersection observers for below-fold section fade-in (not hero — visible immediately)
  useEffect(() => {
    if (isMobile) {
      setProcessVisible(true)
      setBusinessProblemsVisible(true)
      return
    }

    const observerOptions = { threshold: 0.1 }

    const businessProblemsObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setBusinessProblemsVisible(true)
    }, observerOptions)

    const processObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setProcessVisible(true)
    }, observerOptions)

    if (businessProblemsRef.current) businessProblemsObserver.observe(businessProblemsRef.current)
    if (processRef.current) processObserver.observe(processRef.current)
    return () => {
      businessProblemsObserver.disconnect()
      processObserver.disconnect()
    }
  }, [isMobile, routeKey, pageContent?.id])

  // If mobile, render mobile version
  if (isMobile) {
    return (
      <>
        <SEOHead {...pageSeo} />
      <div
        style={{ position: 'absolute', width: '100vw', maxWidth: '100%', margin: '0 auto', background: colors.background.white }}
      >
        <Header />
        {!isPageLoading && cmsNotFound && (
          <p
            role="status"
            style={{
              margin: 0,
              padding: '12px 16px',
              background: '#fff3cd',
              color: '#664d03',
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: '14px',
              lineHeight: '1.4',
              textAlign: 'center',
            }}
          >
            No CMS page for &quot;{serviceId}&quot;. In Strapi, add a service-pages entry with slug{' '}
            <strong>{serviceId}</strong> (see API: filters[slug][$eq]).
          </p>
        )}

        {/* Hero Section - Mobile */}
        <section
          ref={heroRef}
          style={{
            position: 'relative',
            width: '100%',
            minHeight: '650px',
            background: colors.background.dark,
          }}
        >
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: '0',
              top: '0',
              objectFit: 'cover',
            }}
          >
            <source src="/images/6013094_People_Person_1280x720.mp4" type="video/mp4" />
          </video>

          {/* Overlay */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: '0',
              top: '0',
              background: 'rgba(25, 26, 26, 0.9)',
            }}
          />

          {/* Blur Effect */}
          <div
            style={{
              position: 'absolute',
              width: '282px',
              height: '104.37px',
              left: '0px',
              top: '64px',
              background: colors.primary[500],
              opacity: 0.3,
              filter: 'blur(70.7121px)',
            }}
          />

          {/* Gradient Overlay - Transition to next section */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '500px', // Matches the visual height of the fade
              left: '0',
              bottom: '0', // Positioned at the bottom of the hero
              background: 'linear-gradient(180deg, rgba(14, 29, 39, 0) 0%, #07141D 43.18%, #000000 90%)', // Adjusted slightly to ensure deep black at bottom
              pointerEvents: 'none',
              zIndex: 1, // Above video/background
            }}
          />

          {/* Content — full CMS copy wraps naturally */}
          <div
            aria-busy={isPageLoading}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '0 16px 32px',
              gap: '16px',
              position: 'absolute',
              width: '100%',
              left: '0',
              right: '0',
              top: '264px',
              zIndex: 2,
              boxSizing: 'border-box',
            }}
          >
            <h3
              title={pageContent?.title?.toUpperCase()}
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 400,
                fontSize: '18px',
                lineHeight: '20px',
                textAlign: 'center',
                color: colors.neutral[95],
                margin: 0,
                letterSpacing: '5px',
                textTransform: 'uppercase',
                ...cmsParagraph(),
                
              }}
            >
              {pageContent?.title?.toUpperCase() ?? ''}
            </h3>
            <h1
              title={pageContent?.header}
              style={{
                width: '100%',
                fontFamily: "'Segoe UI', sans-serif",
                fontWeight: 700,
                fontSize: '28px',
                lineHeight: '37px',
                textAlign: 'center',
                color: colors.neutral[95],
                margin: 0,
                ...cmsParagraph(),
              }}
            >
              {pageContent?.header ?? ''}
            </h1>
            <p
              title={pageContent?.description}
              style={{
                width: '100%',
                fontFamily: "'Segoe UI', sans-serif",
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '21px',
                textAlign: 'center',
                color: colors.neutral[80],
                margin: 0,
                ...cmsParagraph(),
              }}
            >
              {pageContent?.description ?? ''}
            </p>
            <AuditCtaButton href="#contact" variant="light" compact style={{ ...SERVICE_HERO_CTA_STYLE, alignSelf: 'center' }}>
              Get your Free Audit
            </AuditCtaButton>
          </div>
        </section>

        <MobileProblemCardsSection
          items={problemCards}
          visible={businessProblemsVisible}
          sectionRef={businessProblemsRef}
          sectionTitle={pageContent?.problemCardsSectionTitle}
          cmsReady={!isPageLoading}
        />

        <MobileWorkflowSection items={workflowItems} sectionRef={workflowRef} />

        <MobileApproachSection
          items={approachItems}
          visible={processVisible}
          sectionRef={processRef}
          sectionTitle={pageContent?.approachTitle}
          sectionIntro={pageContent?.approachParagraph}
          cmsReady={!isPageLoading}
        />

        <MobileTechnologySection
          items={technologyItems}
          sectionRef={techRef}
          sectionTitle={pageContent?.technologyTitle}
          intro={pageContent?.technologyParagraph}
          cmsReady={!isPageLoading}
        />


        {/* Contact Section */}
        <div style={{ marginTop: '0' }}>
          <Contact isMobile={true} cmsReady={!isPageLoading} contactInfo={contactInfo} />
        </div>

        {/* Footer */}
        <div style={{ marginTop: '0' }}>
          <Footer isMobile={true} cmsReady={!isPageLoading} contactInfo={contactInfo} />
        </div>
      </div >
      </>
    )
  }

  // Desktop version
  return (
    <>
      <SEOHead {...pageSeo} />
    <div
      className="w-screen overflow-x-hidden flex justify-center bg-white"
      style={{
        height: 'fit-content',
        overflow: 'hidden',
      }}
    >
      <main
        ref={sectionRef}
        className="relative bg-white"
        style={{
          width: '1920px',
          transformOrigin: 'top center',
          transform: `scale(${scale})`,
          height: `${desktopDesignHeight}px`,
          marginBottom: `${-desktopDesignHeight * (1 - scale)}px`,
        }}
        role="main"
        aria-label="Service page content"
      >
        {/* Header */}
        <Header />
        {!isPageLoading && cmsNotFound && (
          <p
            role="status"
            style={{
              position: 'absolute',
              zIndex: 20,
              top: '72px',
              left: '50%',
              transform: 'translateX(-50%)',
              margin: 0,
              padding: '12px 24px',
              maxWidth: '900px',
              background: '#fff3cd',
              color: '#664d03',
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: '16px',
              lineHeight: '1.4',
              textAlign: 'center',
              borderRadius: '8px',
            }}
          >
            No CMS page for &quot;{serviceId}&quot;. In Strapi, set slug to <strong>{serviceId}</strong> on the
            service-pages entry.
          </p>
        )}

        {/* Hero Section */}
        <section
          ref={heroRef}
          style={{
            position: 'relative',
            width: '1920px',
            height: `${heroHeight}px`,
            left: 'calc(50% - 1920px/2)',
            top: '0px',
          }}
        >
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            style={{
              position: 'absolute',
              width: '1920px',
              height: `${heroHeight}px`,
              left: 'calc(50% - 1920px/2)',
              top: '0px',
              objectFit: 'cover',
              background: colors.background.dark,
            }}
          >
            <source src="/images/6013094_People_Person_1280x720.mp4" type="video/mp4" />
          </video>

          {/* Overlay */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              width: '1920px',
              height: `${heroHeight}px`,
              left: 'calc(50% - 1920px/2)',
              top: '0px',
              background: 'rgba(7, 20, 29, 0.77)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />

          {/* Text Content — full CMS copy wraps naturally */}
          <div style={SERVICE_HERO_TEXT_BOX} aria-busy={isPageLoading}>
            <h3
              title={pageContent?.title?.toUpperCase()}
              style={SERVICE_HERO_EYEBROW}
            >
            {pageContent?.title?.toUpperCase() ?? ''}
            </h3>
            <h1
              title={pageContent?.header}
              style={SERVICE_HERO_HEADLINE}
            >
              {pageContent?.header ?? ''}
            </h1>
            <p
              title={pageContent?.description}
              style={SERVICE_HERO_DESC}
            >
              {pageContent?.description ?? ''}
            </p>
            <AuditCtaButton href="#contact" variant="light" style={SERVICE_HERO_CTA_STYLE}>
              Get your Free Audit
            </AuditCtaButton>
          </div>

        </section>

        <DesktopProblemCardsSection
          items={problemCards}
          visible={businessProblemsVisible}
          sectionRef={businessProblemsRef}
          top={businessProblemsTop}
          sectionTitle={pageContent?.problemCardsSectionTitle}
          cmsReady={!isPageLoading}
        />

        <DesktopWorkflowSection items={workflowItems} sectionRef={workflowRef} top={workflowTop} />

        <DesktopApproachSection
          items={approachItems}
          visible={processVisible}
          sectionRef={processRef}
          top={processTop}
          sectionTitle={pageContent?.approachTitle}
          sectionIntro={pageContent?.approachParagraph}
          cmsReady={!isPageLoading}
        >
          <ApproachDecorativeArrows />
        </DesktopApproachSection>

        <DesktopTechnologySection
          items={technologyItems}
          sectionRef={techRef}
          top={techTop}
          sectionTitle={pageContent?.technologyTitle}
          intro={pageContent?.technologyParagraph}
          cmsReady={!isPageLoading}
        />

        {/* Contact Section */}
        <div style={{ position: 'absolute', top: `${contactTop}px`, width: '100%' }}>
          <Contact isMobile={isMobile} cmsReady={!isPageLoading} contactInfo={contactInfo} />
        </div>

        {/* Footer */}
        <div style={{ position: 'absolute', top: `${footerTop}px`, width: '100%' }}>
          <Footer isMobile={isMobile} cmsReady={!isPageLoading} contactInfo={contactInfo} />
        </div>
      </main>
    </div>
    </>
  )
}
